import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ref, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import './PictureFrame.css';

// Helper function to get day of year
const getDayOfYear = (date) => {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date - start;
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor(diff / oneDay);
};

// Tiny placeholder - shows instantly while real image loads
const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f4efe7" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%238b7355" font-size="20" font-family="Arial"%3ELoading...%3C/text%3E%3C/svg%3E';

const PictureFrame = () => {
  const [photos, setPhotos] = useState([]);
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Fetch photos from database
  useEffect(() => {
    const photosRef = ref(database, 'photos/frame');
    const unsubscribe = onValue(photosRef, (snapshot) => {
      if (snapshot.exists()) {
        const photosData = snapshot.val();
        const photosArray = Object.entries(photosData).map(([id, photo]) => ({
          id,
          ...photo,
        }));
        // Sort by creation date (oldest first for consistent rotation)
        photosArray.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        setPhotos(photosArray);
      } else {
        // Use placeholder when no photos uploaded
        setPhotos([{
          id: 'placeholder',
          url: 'https://via.placeholder.com/400x300/f5d5c3/6b5644?text=Upload+Photos',
          caption: 'Upload photos in admin panel',
        }]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Select photo based on day of year (one photo per day)
  useEffect(() => {
    if (photos.length === 0) return;

    const today = new Date();
    const dayOfYear = getDayOfYear(today);
    const photoIndex = dayOfYear % photos.length;

    setCurrentPhoto(photos[photoIndex]);
    setImageLoaded(false); // Reset image loaded state when photo changes
  }, [photos]);

  return (
    <div className="picture-frame">
      <motion.div
        className="frame-container"
        whileHover={{ scale: 1.03 }}
        transition={{ duration: 0.3 }}
      >
        <div className="frame-border">
          <div className="frame-inner">
            {!loading && currentPhoto && (
              <>
                {/* Placeholder image - shows immediately */}
                {!imageLoaded && (
                  <img
                    src={PLACEHOLDER_IMAGE}
                    alt="Loading..."
                    className="daily-photo placeholder-image"
                  />
                )}
                {/* Real image - fades in when loaded */}
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentPhoto?.id}
                    src={currentPhoto?.url}
                    alt={currentPhoto?.caption || 'Daily memory'}
                    className="daily-photo"
                    onLoad={() => setImageLoaded(true)}
                    style={{
                      opacity: imageLoaded ? 1 : 0,
                      position: imageLoaded ? 'relative' : 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%'
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: imageLoaded ? 1 : 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8 }}
                  />
                </AnimatePresence>
              </>
            )}
            {loading && (
              <div className="photo-loading">
                <div className="loading-spinner" />
              </div>
            )}
          </div>

          {/* Frame decorations */}
          <div className="frame-corner frame-corner-tl" />
          <div className="frame-corner frame-corner-tr" />
          <div className="frame-corner frame-corner-bl" />
          <div className="frame-corner frame-corner-br" />
        </div>

        {/* Display "A moment together" or custom caption */}
        <motion.div
          className="photo-caption"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          {currentPhoto?.caption || 'A moment together'}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PictureFrame;
